import { getLiveDetailAjax } from "@/api/live";
import {
  bizInfoParams,
  carrierContextParams,
  CarrierElementType,
  ContentType,
  envContextParams,
} from "@/api/scode/scode_params";
import PosterSwipe from "@/components/poster-swiper";
import { LiveInfo, PreheatInfo } from "@/pages/live/types/types";
import { goToLogin } from "@/utils/checkUrl";
import { useCommonContext } from "@/utils/context/common";
import { MICRO_APP_BASEURL } from "@/utils/platform";
import Track from "@/utils/track";
import useNavigation from "@/utils/useNavigate";
import { Toast } from "antd-mobile";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useShareLive from "../hooks/useShareLive";
import useSubscribe from "../hooks/useSubscribe";
import GoodPreheatContent from "./components/GoodPreheatContent";
import LiveSubscribePop from "./components/LiveSubscribePop";
import styles from "./index.module.less";
const track = Track();

const LivePreheat: React.FC = () => {
  // 页面获取各个路由参数
  const [queryParams] = useSearchParams();
  // 推测liveType表示直播类型，10是普通的商品直播，20是素材或电台直播
  const [liveType, setLiveType] = useState<number>(() =>
    Number(queryParams.get("liveType") || "10"),
  );
  const [liveNo, setLiveNo] = useState<string>(() => queryParams.get("liveNo") || "");
  const [shopId, setShopId] = useState<string>(() => queryParams.get("shopId") || "");
  const [from, setFrom] = useState<string>(() => queryParams.get("from") || "");
  const [ut, setUt] = useState<string>(() => queryParams.get("ut") || "");

  // 声明其他变量
  const livePreheatRef = useRef<HTMLDivElement>(null);

  const [livingInfo, setLivingInfo] = useState<LiveInfo | null>(null);

  // 各种相关钩子
  // 分享相关钩子
  const { shareLiveLink, showSharePoster, setShowSharePoster } = useShareLive({ liveType, shopId });
  // 订阅相关钩子
  const {
    handleSubscribeMixin,
    showSubscribePop,
    setShowSubscribePop,
    subscribeLink,
    subscribeSuccessCbk,
  } = useSubscribe({
    liveType,
  });
  const { navigate } = useNavigation();
  const { userVisitInfo } = useCommonContext();

  // 是否登录
  const hasLogin = userVisitInfo && userVisitInfo.isAuthorized;

  /**
   * 查询直播详情
   */
  const getLiveInfo = useCallback(
    async liveNo => {
      if (!liveNo) {
        console.warn("缺少直播号参数，无法查询直播详情");
        return;
      }
      try {
        const res = await getLiveDetailAjax({ liveNo });
        if (res) {
          // spm在分享时用到
          const _liveInfo = { ...res, liveType, spm: "2106" };
          setLivingInfo(_liveInfo);
          // 根据spm,查询并配置分享信息
          shareLiveLink(_liveInfo, "直播特卖即将开始，快来抢购吧～");
        } else {
          console.error("获取直播详情失败");
        }
      } catch (err) {
        console.error("获取直播详情失败:", err);
      }
    },
    [liveType, shareLiveLink],
  );

  // 同步直播详情
  useEffect(() => {
    window.$live?.ready(() => getLiveInfo(liveNo));
  }, [liveNo, getLiveInfo]);

  /**
   * 订阅开播提醒
   * @param {Function} cb - 订阅成功后的回调
   * @param {Object} res - 直播详情数据
   */
  const onSubScribeLiveStart = (successCbk: () => void, preheatInfo: PreheatInfo) => {
    track.track("btn_click", {
      page_name: "直播预告",
      previous_page_name: queryParams.get("page_name"),
      domain: "直播",
      live_no: liveNo,
      live_name: livingInfo?.liveName,
      btn_name: "预约直播",
    });
    // 未登录则引导登录
    if (!hasLogin) {
      console.log("未登录,需引导登录后才能订阅");
      goToLogin();
      return;
    }
    // 查询公众号信息，拉起公众号二维码弹窗，引导关注公众号（订阅）
    handleSubscribeMixin({ preheatInfo, index: null, successCbk });
  };

  /**
   * 点击底部的“去观看直播”，跳转到直播页面
   * @param {Number} liveStatus - 直播状态，20表示直播中，其他表示直播结束
   */
  const onGoToLivePage = useCallback(
    (liveStatus?: number) => {
      const hashName = liveStatus === 20 ? "living" : "live-end"; // Adjust paths as per your routing
      navigate({
        h5Options: {
          url: `${MICRO_APP_BASEURL}/#/${hashName}`,
          query: {
            liveNo,
            liveType,
          },
        },
      });
    },
    [liveNo, liveType, navigate],
  );

  /**
   * 点击分享按钮，分享直播间
   */
  const onShareLive = async () => {
    if (!livingInfo) {
      console.error("分享直播间失败，livingInfo 为空");
      return;
    }
    setShowSharePoster(true);

    track.track("resource_click", {
      page_name: "直播预告",
      live_no: queryParams.get("liveNo"),
      live_name: livingInfo?.liveTitle,
      live_type: livingInfo?.liveType,
      live_status:
        livingInfo?.liveStatus === 20
          ? "直播中"
          : livingInfo?.liveStatus === 10
            ? "直播预告"
            : "直播结束",
      domain: "直播",
      btn_name: "转发",
    });
  };

  return (
    <div ref={livePreheatRef} className={styles.good_live_preheat_wrap}>
      {/* 素材预告场景尚未迁移，待开发 */}
      {liveType === 20 ? null : (
        <GoodPreheatContent
          liveNo={liveNo}
          shopId={shopId}
          onSubScribe={onSubScribeLiveStart}
          onGoToLive={onGoToLivePage}
          onShare={onShareLive}
        />
      )}

      {/* 关注公众号：店铺服务助手(订阅）弹窗 */}
      {showSubscribePop && (
        <LiveSubscribePop
          subscribeLink={subscribeLink}
          onSubscribePopupClose={() => {
            setShowSubscribePop(false);
          }}
        />
      )}

      {/* 分享直播卡片弹窗 */}
      {showSharePoster && (
        <PosterSwipe
          show={showSharePoster}
          posterData={{
            bizInfo: bizInfoParams({
              contentType: ContentType.XD_LIVE,
              contentId: liveNo,
            }),
            envContext: envContextParams({
              sourceSceneId: liveNo,
            }),
            carrierContext: carrierContextParams([CarrierElementType.h5Poster]),
          }}
          trackOptions={{
            live_no: liveNo,
            live_name: livingInfo?.liveTitle,
            live_type: liveType,
            live_status:
              livingInfo?.liveStatus === 20
                ? "直播中"
                : livingInfo?.liveStatus === 10
                  ? "直播预告"
                  : "直播结束",
          }}
          shareType="XD_LIVE"
          btnConfig={["copy", "edit"]}
          close={() => {
            setShowSharePoster(false);
          }}
        />
      )}
    </div>
  );
};

export default LivePreheat;
